import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import Select, { components } from "react-select";
import { FormikProps } from "formik";
import { Badge, Button, Form } from "react-bootstrap";
import useEcwOrders from "../../../Hooks/useEcwOrders";
import EcwOrderInfo from "../InfoComponents/EcwOrderInfo";
import { ensurePeriod, toggleClass } from "../../../utils/commonUtils";
import {
  CommonEcwComponentProps,
  OrderTypeOptions,
  EcwFormValues,
  EcwOrdersResponse,
  EcwOrder,
  EcwOrderResponse,
  RecordApiMessages,
  getOrderKey,
} from "../EcwConstants";
import { AppDispatch } from "../../../redux/store";
import { useDispatch } from "react-redux";
import {
  setEcwOrderList,
  setEcwSelectedOrderInfo,
} from "../../../redux/ecwOrder/ecwOrderSlice";
import { useModulePermissions, PermissionModule, PermissionIdentifier } from "../../../utils/permissionUtils";
import EcwAddPatientModal from "./EcwAddPatientModal";

interface EcwBasicInfoFormProps extends CommonEcwComponentProps {
  formik: FormikProps<EcwFormValues>;
  recordApiMsgs: RecordApiMessages | null;
  ecwOrderRes: EcwOrderResponse | null;
  handleResetForm: (isReset?: boolean) => void;
}

interface getPatientOptionsParams {
  searchText?: string;
  isOptSelect?: boolean;
  fetchFromFhirApi?: boolean;
}

const EcwBasicInfoForm: React.FC<EcwBasicInfoFormProps> = ({
  formik,
  setLoading,
  recordApiMsgs,
  ecwOrderRes,
  handleResetForm,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [ecwOrders, setEcwOrders] = useState<EcwOrdersResponse>({});
  const [showAddPatientModal, setShowAddPatientModal] = useState<boolean>(false);
  const [selectedPatientOpt, setSelectedPatientOpt] = useState<any | null>(null);
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [searchPatientText, setSearchPatientText] = useState<string>("");
  const [isFetchFromFhirApi, setIsFetchFromFhirApi] = useState<boolean>(false);
  const searchTimeoutRef = useRef<any>(null);

  const {
    getEcwOrdersAPI,
    getEcwOrdersLoading,
    revertEcwOrderAPI,
    revertEcwOrderLoading,
    getEcwPatientsAPI,
    getEcwPatientsLoading,
    createEcwPatientFromFhirAPI,
    createEcwPatientFromFhirLoading,
  } = useEcwOrders();

  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleChange,
    setFieldTouched
  } = formik;

  const selectedOrder = useMemo(() => {
    if (!values.orderId || !ecwOrders?.orders?.length) return null;
    const selectedOrderInfo = ecwOrders?.orders?.find((item: EcwOrder) => {
      const itemOrderId = getOrderKey(item);
      return String(itemOrderId) === String(values.orderId);
    }) || null;
    dispatch(setEcwSelectedOrderInfo(selectedOrderInfo));
    return selectedOrderInfo;
  }, [values.orderId, ecwOrders]);

  useEffect(() => {
    dispatch(setEcwOrderList(ecwOrders));
  }, [dispatch, ecwOrders]);

  useEffect(() => {
    setLoading?.(revertEcwOrderLoading);
  }, [revertEcwOrderLoading, setLoading]);

  useEffect(() => {
    if (touched.patID) {
      setFieldTouched("patID", false);
    }
  }, [values.patID]);

  useEffect(() => {
    if (touched.orderId) {
      setFieldTouched("orderId", false);
    }
  }, [values.orderId]);

  useEffect(() => {
    if (selectedPatientOpt) {
      setPatientOptions([selectedPatientOpt]);
    } else {
      setPatientOptions([]);
    }
  }, [selectedPatientOpt]);

  useEffect(() => {
    const isEmrRecordFound = patientOptions?.some(item => !item?.value?.isSRXRecord);
    setIsFetchFromFhirApi(isEmrRecordFound);
  }, [patientOptions]);

  useEffect(() => {
    if (!ecwOrderRes || !selectedOrder) return;

    const { isOrderProcessed, ecwResponseMessage } = ecwOrderRes;

    setEcwOrders((prev) => ({
      ...prev,
      orders: prev.orders?.map((order) =>
        order.id === selectedOrder.id
          ? {
            ...order,
            colour: isOrderProcessed ? "green" : "red",
            isSuccess: isOrderProcessed,
            status: isOrderProcessed ? "Administered" : order.status,
            ecwResponseMessage: isOrderProcessed ? null : ecwResponseMessage,
          }
          : order
      ),
    }));
  }, [ecwOrderRes]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const getPatientOptions = useCallback(async (params?: getPatientOptionsParams) => {
    const { searchText = "", isOptSelect = false, fetchFromFhirApi = false } = params || {};
    if (!searchText) return;

    handlePatientChange(null);
    handleOrderChange(null);
    handleResetForm(true);
    try {
      const res: any = await getEcwPatientsAPI({ searchText, fetchFromFhirApi });
      if (res && _.isArray(res) && res.length > 0) {
        const patients = res.map((item) => ({
          value: item,
          label: `${item.lastName}, ${item.firstName} ${item.mi ?? ""} (${item.patientCode})`,
        }));
        setPatientOptions(patients);
        if (isOptSelect && patients.length) {
          const newOpt = patients[0];
          handlePatientChange(newOpt);
        }
        return patients;
      } else {
        setPatientOptions([]);
        return [];
      }
    } catch (err: any) {
      console.error(err);
      setPatientOptions([]);
      return [];
    }
  }, [getEcwPatientsAPI, handleResetForm]);

  const getEcwOrdersList = useCallback(async (patientId: string) => {
    if (!patientId) {
      setEcwOrders({});
      return;
    }
    try {
      const res = await getEcwOrdersAPI({ id: patientId });
      setEcwOrders(res ?? {});
    } catch (err) {
      console.error(err);
      setEcwOrders({});
    }
  }, [getEcwOrdersAPI]);

  const createPatientFromFhir = async (patientInfo: any) => {
    try {
      const res: any = await createEcwPatientFromFhirAPI(patientInfo);
      if (res?.success && res?.result) {
        handleOrderChange(null);
        getEcwOrdersList(res?.result);
      }
    } catch (err: any) {
      console.error(err);
      return [];
    }
  }

  const handlePatientChange = useCallback((option: any) => {
    const patient = option?.value || null;

    setSelectedPatientOpt(option || null);

    const fields: Partial<EcwFormValues> = {
      patID: patient?.patientCode ?? "",
      patFName: patient?.firstName ?? "",
      patMName: patient?.mi ?? "",
      patLName: patient?.lastName ?? "",
      patDOB: patient?.dob ?? "",
      patGender: patient?.gender ?? "",
    };

    Object.entries(fields).forEach(([key, val]) => setFieldValue(key, val));

    if (!patient) return;

    if (patient.patientCode !== values.patID) {
      if (patient?.isSRXRecord) {
        handleOrderChange(null);
        getEcwOrdersList(patient.id);
      } else {
        createPatientFromFhir(patient);
      }
    }
  }, [setFieldValue, values.patID, getEcwOrdersList]);

  const toggleShowAddPatientModal = useCallback(() => {
    toggleClass();
    setShowAddPatientModal((prev) => !prev);
  }, []);

  const handlePatientAddSuccess = useCallback((patientRes: any) => {
    if (patientRes?.patientCode) {
      getPatientOptions({ searchText: patientRes.patientCode, isOptSelect: true });
      setSelectedPatientOpt({
        value: patientRes,
        label: `${patientRes.lastName}, ${patientRes.firstName} ${patientRes.mi ?? ""} (${patientRes.patientCode})`,
      });
    }
    toggleShowAddPatientModal();
  }, [getPatientOptions, toggleShowAddPatientModal]);

  const handleOrderChange = useCallback((selectedItem: any) => {
    const order = selectedItem?.value ?? null;

    const medicationRequest = order?.medicationRequest ?? null;

    const fields: Partial<EcwFormValues> = {
      orderId: medicationRequest ? (medicationRequest?.rxOrderNo ?? "") : (order?.immId ?? ""),
      orderType: order?.orderType ?? "",
      hcpid: order?.provider?.npi ?? "",
      hcpfName: order?.provider?.firstName ?? "",
      hcplName: order?.provider?.lastName ?? "",
      hcpNum: order?.provider?.deaNumber ?? "",
      location: order?.facility?.facilityId ? `${order?.facility?.facilityId}:${order?.facility?.facilityName}` : "",
      encounterDate: order?.encounterDate ?? "",
      encounterId: order?.encounterId ?? "",
      facilityGuid: order?.facility?.guid ?? "",
      facilityName: order?.facility?.facilityName ?? "",
    };

    Object.entries(fields).forEach(([key, val]) => setFieldValue(key, val));
  }, [setFieldValue]);

  const handleRevertOrder = useCallback(async (orderId: string | number) => {
    if (!orderId) return;
    try {
      const revertRes = await revertEcwOrderAPI({ id: orderId });
      const revicedOrderInfo = revertRes?.result;

      if (revicedOrderInfo) {
        setEcwOrders((prev) => ({
          ...prev,
          orders: prev.orders?.map((order) =>
            order.id === revicedOrderInfo.id
              ? {
                ...order,
                colour: revicedOrderInfo?.colour,
                isSuccess: revicedOrderInfo?.isSuccess,
                status: revicedOrderInfo?.status,
              }
              : order
          ),
        }));
      }
    } catch (error) {
      console.error("Error occurred while reverting order:", error);
    }
  }, []);

  const handlePatientInputChange = useCallback((newValue: string) => {
    setSearchPatientText(newValue);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (newValue) {
      searchTimeoutRef.current = setTimeout(() => {
        getPatientOptions({ searchText: newValue });
      }, 300);
    } else {
      setPatientOptions([]);
    }
  }, [getPatientOptions]);

  const handleRetrieveFromEmr = useCallback(() => {
    setIsFetchFromFhirApi(true);
    getPatientOptions({ searchText: searchPatientText, fetchFromFhirApi: true });
  }, [getPatientOptions, searchPatientText]);

  const customComponents = useMemo(() => ({
    DropdownIndicator: () => null,
    Menu: (props: any) => (
      <components.Menu {...props}>
        {props.children}
        {(searchPatientText && !isFetchFromFhirApi) && (
          <div className="p-2 border-top d-flex justify-content-center">
            <Button
              size="sm"
              variant="link"
              className="p-0 m-0"
              onClick={handleRetrieveFromEmr}
              style={{ pointerEvents: getEcwPatientsLoading ? "none" : "auto" }}
            >
              Retrieve from EMR
            </Button>
          </div>
        )}
      </components.Menu>
    )
  }), [getEcwPatientsLoading, handleRetrieveFromEmr, searchPatientText, isFetchFromFhirApi]);

  const ecwOrdersOptions = useMemo(() => {
    return (
      ecwOrders?.orders?.map((item: EcwOrder) => {
        const medicationRequest = item?.medicationRequest;
        const itemName = medicationRequest?.medicationName || item?.vaccineName;
        const orderId = getOrderKey(item);
        return {
          label: `${item?.encounterDate} - ${itemName} (Enc. Id: ${item?.encounterId}, Order Id: ${orderId})`,
          value: item,
        };
      }) || []
    );
  }, [ecwOrders?.orders]);

  const isPermitted = useModulePermissions(PermissionModule.ECW_ORDER_ADMINISTRATION);
  const isAddPatientPermitted = isPermitted(PermissionIdentifier.Add_PATIENT_ON_ECW);

  return (
    <>
      <div className="basic-info-box">
        <h4 className="form-section-title">
          Basic Information
          <span className={`order-type-symbol ${values.orderType}`}>
            {values.orderType}
          </span>
        </h4>
        <Form.Group className="form-field">
          <Form.Label>
            Patient ID*{" "}
            {isAddPatientPermitted && (
              <Badge bg="primary" onClick={toggleShowAddPatientModal}>
                Add Patient
              </Badge>
            )}
          </Form.Label>
          <Select
            value={selectedPatientOpt}
            isLoading={getEcwPatientsLoading}
            options={patientOptions}
            classNamePrefix="react-select"
            placeholder="Enter Patient ID"
            onChange={handlePatientChange}
            onBlur={() => setFieldTouched("patID", true, true)}
            onInputChange={handlePatientInputChange}
            components={customComponents}
            className={`${touched.patID && errors.patID ? "is-invalid-border" : ""} react-select-container`}
          />
          {errors.patID && touched.patID && (
            <Form.Control.Feedback type="invalid">
              {errors.patID}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group className="form-field">
          <Form.Label>Order*</Form.Label>
          <Select
            name="orderId"
            options={ecwOrdersOptions}
            isLoading={getEcwOrdersLoading}
            classNamePrefix="react-select"
            placeholder="Please select order"
            onChange={handleOrderChange}
            onBlur={() => setFieldTouched("orderId", true, true)}
            value={
              ecwOrdersOptions.find(
                (opt) => String(getOrderKey(opt.value)) === String(values.orderId)
              ) || null
            }
            className={`${touched.orderId && errors.orderId ? "is-invalid-border" : ""} react-select-container`}
          />
          {errors.orderId && touched.orderId && (
            <Form.Control.Feedback type="invalid">
              {errors.orderId}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group className="form-field">
          <Form.Label>Order Type</Form.Label>
          <div className="radio-group">
            {OrderTypeOptions.map((item) => (
              <Form.Check
                inline
                disabled
                key={item.value}
                type="radio"
                id={`orderType-${item.value}`}
                name="orderType"
                label={item.label}
                value={item.value}
                onChange={handleChange}
                checked={values.orderType === item.value}
              />
            ))}
          </div>
        </Form.Group>

        <EcwOrderInfo
          ecwOrderList={ecwOrders}
          selectedOrderInfo={selectedOrder}
          handleRevertOrder={handleRevertOrder}
        />

        {recordApiMsgs && (
          <ul className="d-flex flex-column">
            {recordApiMsgs.success && (
              <li className="text-success fw-semibold">
                {ensurePeriod(recordApiMsgs.success)}
              </li>
            )}
            {recordApiMsgs.error?.map((err, index) => (
              <li key={index} className="text-danger fw-semibold">
                {ensurePeriod(err)}
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAddPatientModal && (
        <EcwAddPatientModal
          show={showAddPatientModal}
          onClose={toggleShowAddPatientModal}
          onSuccess={handlePatientAddSuccess}
          setLoading={setLoading}
        />
      )}
    </>
  );
};

export default EcwBasicInfoForm;
